import { likedFolderService, likeGoodsService } from '../../../api';
import { bridge, delay, isIOS, isWeb } from '../../../utils';
import { isAndroid } from '../../../utils/isAndroid';
import { useAsyncEffect } from '../../../utils/useAsyncEffect';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type RefObject } from 'react';
import { Dimensions, Keyboard, TextInput } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { queryKey } from '../../../constant';
import { isAxiosError } from 'axios';
import {
  BottomSheetContainer,
  ColorValues,
  createUseBottomSheetModal,
  Flex,
  KeyboardAvoiding,
  PrimaryButton,
  Typography,
} from '@banhala/ui-kit';
import Toast from 'react-native-toast-message';
import { toast } from '../toast';

type UseCreateNewLikeFolderBottomSheet = {
  goodsSno: number;
  listParams?: string;
  screen: string;
};
export const useCreateNewLikeFolderBottomSheet = createUseBottomSheetModal<
  { goodsSno: number },
  UseCreateNewLikeFolderBottomSheet
>(({ confirm, cancel, param: { goodsSno, listParams, screen } }) => {
  const { bottom, top } = useSafeAreaInsets();
  const [title, setTitle] = useState('');
  const ref = useRef<TextInput>(null);
  useKeyboardFocus(ref);
  const bottomSheetMinHeightInNative =
    (Dimensions.get('window').height - bottom - top) / 3;
  const bottomSheetMinHeightInWeb = useRef(Dimensions.get('window').height / 3);

  const { mutateAsync: mutateAsyncCreateLikeFolder } = useMutation({
    mutationFn: likedFolderService.post,
    throwOnError: false,
  });
  const { mutateAsync: mutateAsyncLikeGoods } = useMutation({
    mutationFn: likeGoodsService.post,
    throwOnError: false,
  });

  const queryClient = useQueryClient();

  return (
    <KeyboardAvoiding>
      <BottomSheetContainer
        onPressClose={() => {
          Keyboard.dismiss();
          cancel();
        }}
        minHeight={
          isWeb
            ? bottomSheetMinHeightInWeb.current
            : isIOS
              ? bottomSheetMinHeightInNative
              : 0
        }
        title="새 서랍 만들기"
      >
        <Flex.Column
          position={'relative'}
          flex={isAndroid ? undefined : 1}
          padding={{ vertical: 16 }}
        >
          <Flex.Column
            padding={{ horizontal: 10 }}
            height={isAndroid ? 78 : undefined}
          >
            <TextInput
              ref={ref}
              style={{
                fontSize: 16,
                outlineWidth: 0,
              }}
              placeholder="서랍 이름은 최소 한 글자 이상 입력해주세요."
              placeholderTextColor={ColorValues.gray50}
              autoFocus={!isWeb}
              multiline
              numberOfLines={2}
              onChangeText={value => {
                setTitle(value);
              }}
              maxLength={30}
            />
            <Flex.Row position={'absolute'} right={10} top={60}>
              <Typography
                color={'content_secondary'}
              >{`${title.length}`}</Typography>
              <Typography color={'content_disabled'}>{` / 30`}</Typography>
            </Flex.Row>
          </Flex.Column>
        </Flex.Column>
        <PrimaryButton
          variant={'fill'}
          color={'pink'}
          removeBorderRadius={true}
          // disabled={title.length === 0}
          state={title.length === 0 ? 'disabled' : undefined}
          onPress={async () => {
            try {
              const {
                data: { sno },
              } = await mutateAsyncCreateLikeFolder({
                title,
              });
              await mutateAsyncLikeGoods({
                list_params_referrer: listParams,
                sno: goodsSno,
                folder_sno: sno,
              });
              toast({
                folderName: title,
                folderSno: sno,
                screen,
              });
              queryClient.invalidateQueries({
                queryKey: queryKey.getInfiniteLikeFolders,
              });
              queryClient.invalidateQueries({
                queryKey: queryKey.getInfiniteRecentlyViewedGoods,
              });
              queryClient.invalidateQueries({
                queryKey: queryKey.getMemberInfoQuery,
              });

              bridge.likeGoods({
                goods_sno: goodsSno,
                is_liked: true,
                folder_sno: sno,
              });
              confirm({ goodsSno });
            } catch (error) {
              cancel();
              await delay(500);
              let errorMessage = '오류가 발생했어요';
              if (isAxiosError(error)) {
                errorMessage =
                  error.response?.data?.message ?? '오류가 발생했어요';
              }
              Toast.show({
                type: 'success',
                text1: errorMessage,
                position: 'bottom',
              });
            }
          }}
        >
          완료
        </PrimaryButton>
      </BottomSheetContainer>
    </KeyboardAvoiding>
  );
});

/**
 * - 바텀시트가 올라올 떄 키보드가 보이게 하기 위한 훅
 * - 안드로이드에서는 onFocus가 동작하지 않아 추가
 */
const useKeyboardFocus = (ref: RefObject<TextInput | null>) => {
  useAsyncEffect(async () => {
    if (!isAndroid) {
      return;
    }

    await delay(100);
    ref.current?.blur();
    ref.current?.focus();
  }, []);
};
